// devices domain types. Split out of types/index.ts (2,600 lines) on 2026-07-03.
// Import via '@/types' as before — index.ts re-exports everything.


import type { PosOrderStatus } from './pos.types';

export enum PrintAgentStatus {
  ONLINE = 'ONLINE',
  OFFLINE = 'OFFLINE',
}

export enum PrinterType {
  RECEIPT = 'RECEIPT',
  LABEL = 'LABEL',
  PDF = 'PDF',
}

export enum PrinterConnectionType {
  USB = 'USB',
  NETWORK = 'NETWORK',
}

export enum PaperWidth {
  MM_58 = 'MM_58',
  MM_80 = 'MM_80',
}

export enum PrintJobStatus {
  PENDING = 'PENDING',
  SENT = 'SENT',
  PRINTED = 'PRINTED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
}

export enum PrintJobType {
  RECEIPT = 'RECEIPT',
  LABEL = 'LABEL',
  RAW = 'RAW',
}

export enum PrintTemplateType {
  RECEIPT = 'RECEIPT',
  LABEL = 'LABEL',
  RAW = 'RAW',
  SHIFT_SUMMARY = 'SHIFT_SUMMARY',
  MANIFEST = 'MANIFEST',
}

export interface PrintAgent {
  id: string
  shopId: string
  name: string
  status: PrintAgentStatus
  pairCode: string | null
  isPaired: boolean
  pairedAt: string | null
  lastSeenAt: string | null
  version: string | null
  platform: string | null
  ipAddress?: string | null
  createdAt: string
  updatedAt: string
}

export interface Printer {
  id: string
  shopId: string
  agentId: string
  printAgent?: PrintAgent
  name: string
  type: PrinterType
  connectionType: PrinterConnectionType
  identifier?: string | null
  address?: string | null
  port?: string | null
  macAddress?: string | null
  paperWidth: PaperWidth | null
  labelWidthMm?: number | null
  labelHeightMm?: number | null
  dpi?: number | null
  labelSize?: string | null
  make?: string | null
  model?: string | null
  serialNumber?: string | null
  isDefault: boolean
  isActive: boolean
  status?: string | null
  createdAt: string
  updatedAt: string
}

export interface PrintJob {
  id: string
  shopId: string
  printerId: string
  agentId: string
  printer?: Printer
  printAgent?: PrintAgent
  jobType: PrintJobType
  status: PrintJobStatus
  title: string | null
  payload: Record<string, unknown> | null
  rawPayload?: string | null
  preview?: string | null
  errorMessage: string | null
  retries: number
  maxRetries: number
  copies?: number
  printedAt: string | null
  sentAt?: string | null
  createdAt: string
  updatedAt: string
}

export type LabelSize =
  // 1-inch widths
  | '1.25x0.25'
  | '1.25x2.25'
  | '1.5x0.25'
  | '1.5x0.5'
  | '1.5x1'
  | '1.5x2'
  // 2-inch widths
  | '2x0.361'
  | '2x0.5'
  | '2x1'
  | '2x1.25'
  | '2x4'
  | '2x5.5'
  | '2.25x0.5'
  | '2.25x1.5'
  | '2.25x4'
  | '2.25x5.5'
  | '2.375x5.5'
  | '2.5x1'
  | '2.5x2'
  | '2.75x1.25'
  // 3-inch widths
  | '3x1'
  | '3x1.25'
  | '3x2'
  | '3x3'
  | '3x5'
  | '3.25x2'
  | '3.25x5'
  | '3.25x5.5'
  | '3.25x5.819'
  | '3.25x7.819'
  | '3.5x1'
  // 4-inch widths
  | '4x1'
  | '4x2'
  | '4x2.5'
  | '4x3'
  | '4x4'
  | '4x5'
  | '4x6'
  | '4x13'
  // 6-inch widths
  | '6x1'
  | '6x2'
  | '6x3'
  | '6x5'

export interface PrintTemplateVariant {
  id: string
  templateId: string
  name: string
  content: string
  compatibleSizes: LabelSize[] | null
  previewUrl: string | null
  isDefault: boolean
  sortOrder: number
  variables?: { key: string; required: boolean }[]
  createdAt: string
  updatedAt: string
}

export interface PrintTemplate {
  id: string
  organizationId: string | null
  shopId: string | null
  name: string
  type: PrintTemplateType
  description: string | null
  isDefault: boolean
  isSystem: boolean
  isActive: boolean
  variantCount?: number
  variants?: PrintTemplateVariant[]
  createdAt: string
  updatedAt: string
}

export interface CreatePrintAgentPayload {
  name: string
}

export interface UpdatePrintAgentPayload {
  name?: string
}

export interface CreatePrinterPayload {
  printAgentId: string
  name: string
  type: PrinterType
  connectionType: PrinterConnectionType
  address?: string
  paperWidth?: PaperWidth
  isDefault?: boolean
  isActive?: boolean
}

export interface UpdatePrinterPayload extends Partial<CreatePrinterPayload> {}

export interface ReceiptItem {
  name: string
  sku: string
  quantity: number
  unitPriceCents: number
  totalCents: number
}

export interface ReceiptData {
  saleId: string
  saleNumber: string
  cashierId: string
  cashierName: string
  items: ReceiptItem[]
  subtotalCents: number
  taxCents: number
  totalCents: number
  paymentMethod: string
  transactionId: string
  saleDate?: string
}

export interface CreatePrintJobReceiptPayload {
  shopId: string
  printerId?: string
  variantId?: string
  templateId?: string
  copies?: number
  receiptData: ReceiptData
}

export interface CreatePrintJobLabelPayload {
  shopId: string
  printerId: string
  packageId?: string
  templateId?: string
  variantId?: string
  labelData?: Record<string, string>
}

export interface CreatePrintJobRawPayload {
  shopId: string
  printerId: string
  title: string
  rawHtml: string
}

export interface CreatePrintTemplatePayload {
  name: string
  type: PrintTemplateType
  description?: string
  shopId?: string
  isDefault?: boolean
}

export interface UpdatePrintTemplatePayload {
  name?: string
  description?: string
  isDefault?: boolean
  isActive?: boolean
}

export interface CreatePrintTemplateVariantPayload {
  name: string
  content: string
  compatibleSizes?: LabelSize[]
  previewUrl?: string
  isDefault?: boolean
  sortOrder?: number
}

export interface UpdatePrintTemplateVariantPayload
  extends Partial<CreatePrintTemplateVariantPayload> {}

export interface PrintTemplateVariable {
  key: string
  required: boolean
}

export interface RenderTemplatePayload {
  templateId: string
  variantId?: string
  labelSize?: LabelSize
  variables?: Record<string, unknown>
}

export interface ValidateReceiptHtmlPayload {
  html: string
  paperWidthMm: 58 | 80
}

export interface ValidateReceiptHtmlResult {
  valid: boolean
  wasModified: boolean
  detectedIssues: string[]
  sanitizedHtml: string
  preview: {
    image: string // raw base64 PNG (no data URI prefix)
    width: number
    height: number
  }
}

export interface PosFeatureFlag {
  key: string
  enabled: boolean
  rolloutPercent?: number | null
  metadata?: Record<string, unknown> | null
  global?: boolean
}

export interface PosFeatureFlagEnabledResponse {
  enabled: boolean
}

export interface PosRegister {
  id: string
  name: string
  code: string
  locationId?: string | null
  printerId?: string | null
  printTemplateId?: string | null
  printer?: { id: string; name: string; type: string; isDefault: boolean; isActive: boolean } | null
  printTemplate?: { id: string; name: string; type: string; isDefault: boolean; isActive: boolean } | null
  defaultFloatCents?: number | null
  isActive?: boolean
  createdAt?: string
  updatedAt?: string
  activeShift?: { id: string; userId: string; openedAt: string; cashierName: string | null } | null
}

export interface PosCreateRegisterInput {
  name: string
  code: string
  locationId: string
  printerId?: string | null
  printTemplateId?: string | null
  defaultFloatCents?: number | null
}

export interface PosUpdateRegisterInput {
  name?: string
  code?: string
  locationId?: string
  printerId?: string | null
  printTemplateId?: string | null
  isActive?: boolean
  defaultFloatCents?: number | null
}

export type PosShiftStatus = 'OPEN' | 'CLOSED'

export type PosShiftCloseReason = 'END_OF_SHIFT' | 'HANDOFF' | 'FORCED_CLOSE'

export interface PosRegisterShift {
  /** On close: unpaid online orders handed back to the Online orders list */
  releasedOnlineOrders?: number
  id: string
  registerId: string
  userId?: string | null
  drawerSessionId?: string | null
  initialDrawerSessionId?: string | null
  status?: PosShiftStatus
  openedAt?: string
  closedAt?: string | null
  closeReason?: 'END_OF_SHIFT' | 'HANDOFF' | 'FORCED_CLOSE' | null
  forcedByUserId?: string | null
  ordersCount?: number
  grossSalesCents?: number
  netSalesCents?: number
  refundsCents?: number
  tipsCents?: number
  notes?: string | null
  register?: PosRegister | null
  user?: {
    id: string
    name?: string | null
  } | null
}

export interface PosShiftReportTenderRow {
  type: string
  count: number
  amountCents: number
}

export interface PosShiftReportSales {
  salesCount: number
  grossSalesCents: number
  discountCents: number
  taxCents: number
  feeCents: number
  tipCents: number
  netSalesCents: number
  refundsCount: number
  refundsCents: number
  tenderBreakdown: PosShiftReportTenderRow[]
}

export interface PosShiftReportMovement {
  type: string
  amountCents: number
  reason: string
  createdAt: string
}

export interface PosShiftReportDrawerSession {
  id: string
  status: PosDrawerStatus
  openedAt: string
  closedAt: string | null
  openingFloatCents: number
  expectedCashCents: number
  countedCashCents: number | null
  varianceCents: number | null
}

export interface PosShiftReportCash {
  openingFloatCents: number
  cashSalesCents: number
  payInsCents: number
  payOutsCents: number
  cashDropsCents: number
  adjustmentsCents: number
  expectedCashCents: number
  countedCashCents: number | null
  varianceCents: number | null
  movements: PosShiftReportMovement[]
  drawerSessions: PosShiftReportDrawerSession[]
}

export interface PosShiftReportTransaction {
  id: string
  number: string
  status: PosOrderStatus
  completedAt: string | null
  subtotalCents: number
  discountCents: number
  taxCents: number
  tipCents: number
  totalCents: number
  type?: string
  channel?: string
  customerName?: string | null
  paymentTypes?: string[]
  itemsCount?: number
  isRefund?: boolean
}

export interface PosShiftReport {
  shift: {
    id: string
    registerId: string
    userId: string
    status: PosShiftStatus
    openedAt: string
    closedAt: string | null
    closeReason: 'END_OF_SHIFT' | 'HANDOFF' | 'FORCED_CLOSE' | null
    forcedReason: string | null
    notes: string | null
    durationMinutes: number
  }
  cashier: { id: string; name: string } | null
  forcedBy: { id: string; name: string } | null
  register: { id: string; name: string; code: string } | null
  sales: PosShiftReportSales
  /** Extra figures for the full Session summary (older API builds omit it). */
  summary?: PosShiftReportSummary
  cash: PosShiftReportCash
  transactions: PosShiftReportTransaction[]
}

export interface PosShiftReportSummary {
  taxes: Array<{ name: string; rateBp: number; amountCents: number }>
  taxTotalCents: number
  cashTenderedCents: number
  changeGivenCents: number
}

export type PosDrawerStatus = 'OPEN' | 'CLOSED' | 'RECONCILED' | 'DISPUTED' | 'PENDING_RECONCILIATION'

export interface PosDrawerSession {
  id: string
  registerId: string
  registerShiftId?: string | null
  status?: PosDrawerStatus
  openingFloatCents?: number | null
  expectedCashCents?: number | null
  notes?: string | null
  openedAt?: string
  closedAt?: string | null
  countedCashCents?: number | null
  varianceCents?: number | null
  /** Advisory flag from close: 'RED' when |variance| ≥ the cash-management red threshold. */
  varianceStatus?: 'OK' | 'RED'
  /** Advisory: drawer cash has passed the soft max-drawer-cash cap; suggest a cash drop. */
  cashDropRecommended?: boolean
  register?: PosRegister | null
}

export interface PosOpenShiftInput {
  registerId: string
  initialDrawerSessionId?: string | null
  notes?: string | null
}

export interface PosOpenDrawerInput {
  registerId: string
  openingFloatCents: number
  parentDrawerSessionId?: string | null
  notes?: string | null
}

export interface PosCloseShiftInput {
  closeReason: 'END_OF_SHIFT' | 'HANDOFF' | 'FORCED_CLOSE'
  notes?: string | null
}

export interface PosForceCloseShiftInput {
  reason: string
  /// Manager-PIN step-up token (SHIFT_FORCE_CLOSE) for the on-floor path.
  managerOverrideId?: string | null
}

export interface PosForceCloseShiftResult {
  shift: PosRegisterShift
  parkedDrawerIds: string[]
}
